import React from 'react';
import { useSelector } from 'react-redux';
import { getMarket } from '../selectors';
import Decimal from 'decimal.js-light';

import { IMPERIAL_SYSTEM } from '../constants';

import { getMeasurementSystemFromLocale } from '../utils/l10n';

import { getL10nNumber } from './L10nNumber';

type Props = {
  market?: string;
  value: string | number;
  decimals?: number;
  unit?: string;
  locale?: string;
};

const DEFAULT_DECIMALS = 0; // round to 0 decimals places

const UNIT_SIZE_AUTO = 'auto'; // select best unit size based on the given value
const UNIT_SIZE_SMALL = 'small'; // centimeters or inches
const UNIT_SIZE_MEDIUM = 'medium'; // meters or feet
const UNIT_SIZE_LARGE = 'large'; // kilometers or miles

const DEFAULT_UNIT_SIZE = UNIT_SIZE_AUTO;

const CONVERSION_KM_TO_MILES = 0.621371;
const CONVERSION_METER_TO_FEET = 3.28084;
const CONVERSION_CM_TO_INCHES = 0.393701;

function getL10nDistance(
  market: string,
  value: string | number,

  decimals = DEFAULT_DECIMALS,
  unit = DEFAULT_UNIT_SIZE,
) {
  const numberDecimal = new Decimal(value);
  const measurementSystem = getMeasurementSystemFromLocale(market);
  let detectedUnit = unit;
  if (measurementSystem === IMPERIAL_SYSTEM) {
    if (unit === UNIT_SIZE_AUTO) {
      detectedUnit = numberDecimal
        .div(1000)
        .mul(CONVERSION_KM_TO_MILES)
        .gte(1)
        ? UNIT_SIZE_LARGE
        : numberDecimal.mul(CONVERSION_METER_TO_FEET).gte(1)
        ? UNIT_SIZE_MEDIUM
        : UNIT_SIZE_SMALL;
    }
    if (detectedUnit === UNIT_SIZE_LARGE) {
      return `${getL10nNumber(
        market,
        numberDecimal.div(1000).mul(CONVERSION_KM_TO_MILES),
        decimals,
      )}ml`;
    } else if (detectedUnit === UNIT_SIZE_SMALL) {
      return `${getL10nNumber(
        market,
        numberDecimal.mul(1000).mul(CONVERSION_CM_TO_INCHES),
        decimals,
      )}in`;
    }
    return `${getL10nNumber(market, numberDecimal.mul(CONVERSION_METER_TO_FEET), decimals)}ft`;
  }
  if (unit === UNIT_SIZE_AUTO) {
    detectedUnit = numberDecimal.div(1000).gte(1)
      ? UNIT_SIZE_LARGE
      : numberDecimal.gte(1)
      ? UNIT_SIZE_MEDIUM
      : UNIT_SIZE_SMALL;
  }
  if (detectedUnit === UNIT_SIZE_LARGE) {
    return `${getL10nNumber(market, numberDecimal.div(1000), decimals)}km`;
  } else if (detectedUnit === UNIT_SIZE_SMALL) {
    return `${getL10nNumber(market, numberDecimal.mul(1000), decimals)}cm`;
  }
  return `${getL10nNumber(market, numberDecimal, decimals)}m`;
}

function L10nDistance({ market, value, decimals, unit }: Props) {
  const marketFromStore = useSelector(getMarket)?.marketCode || '';
  return <span>{getL10nDistance(market || marketFromStore, value, decimals, unit)}</span>;
}

L10nDistance.defaultProps = {
  decimals: DEFAULT_DECIMALS,
  unit: DEFAULT_UNIT_SIZE,
};

export default L10nDistance;
